/* eslint-disable @typescript-eslint/no-explicit-any */
import { stringify } from 'qs'
import CryptoJS from 'crypto-js'
import { SupportedMultiLanguage, mergeLanguageObject } from '@/types/multiLanguage'
import { JSEncrypt } from 'jsencrypt'
import LGSdk from '@/utils/languageSDK/index'

export const cryptRAS = {
  /**
   * 生成随机 AES 密钥
   * @returns Base64 编码的 AES 密钥
   */
  generateAesKey(): string {
    const key = CryptoJS.lib.WordArray.random(16) // 128 位 AES 密钥
    return CryptoJS.enc.Base64.stringify(key)
  },

  /**
   * 使用 AES 加密数据
   * @param data 待加密数据
   * @param key Base64 编码的 AES 密钥
   * @returns 加密后的数据，包含 IV（Base64 编码）
   */
  aesEncrypt(data: string, key: string): string {
    const aesKey = CryptoJS.enc.Base64.parse(key)
    const iv = CryptoJS.lib.WordArray.random(16) // 生成随机 IV
    const encrypted = CryptoJS.AES.encrypt(data, aesKey, { iv: iv }).toString()
    return `${CryptoJS.enc.Base64.stringify(iv)}:${encrypted}`
  },

  /**
   * 使用 AES 解密数据
   * @param encryptedData 包含 IV 的加密数据（Base64 编码）
   * @param key Base64 编码的 AES 密钥
   * @returns 解密后的数据
   */
  aesDecrypt(encryptedData: string, key: string): string {
    const [encodedIv, encrypted] = encryptedData.split(':')
    const iv = CryptoJS.enc.Base64.parse(encodedIv)
    const aesKey = CryptoJS.enc.Base64.parse(key)
    const decrypted = CryptoJS.AES.decrypt(encrypted, aesKey, { iv: iv }).toString(CryptoJS.enc.Utf8)
    return decrypted
  },

  /**
   * 使用 RSA 加密 AES 密钥
   * @param word 待加密的 AES 密钥（Base64 编码）
   * @param publicKey RSA 公钥（PEM 格式）
   * @returns 加密后的 AES 密钥（Base64 编码）
   */
  rsaEncrypt(word: string, publicKey: string): string {
    const rsa = new JSEncrypt()
    rsa.setPublicKey(publicKey)
    const encrypted = rsa.encrypt(word)
    return encrypted || ''
  },

  /**
   * 使用 RSA 解密 AES 密钥
   * @param encryptedWord 加密后的 AES 密钥（Base64 编码）
   * @param privateKey RSA 私钥（PEM 格式）
   * @returns 解密后的 AES 密钥（Base64 编码）
   */
  rsaDecrypt(encryptedWord: string, privateKey: string): string {
    const rsa = new JSEncrypt()
    rsa.setPrivateKey(privateKey)
    const decrypted = rsa.decrypt(encryptedWord)
    return decrypted || ''
  },

  /**
   * 使用混合加密加密数据
   * @param data 待加密的数据
   * @param publicKey RSA 公钥（PEM 格式）
   * @returns 加密后的数据（Base64 编码）
   */
  encrypt(data: string, publicKey: string): string {
    const aesKey = this.generateAesKey()
    const encryptedData = this.aesEncrypt(data, aesKey)
    const encryptedKey = this.rsaEncrypt(aesKey, publicKey)
    return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(JSON.stringify({ encryptedKey, encryptedData })))
  },

  /**
   * 使用混合加密解密数据
   * @param encryptedMessage 加密后的数据（Base64 编码）
   * @param privateKey RSA 私钥（PEM 格式）
   * @returns 解密后的数据
   */
  decrypt(encryptedMessage: string, privateKey: string): string {
    const decodedMessage = JSON.parse(CryptoJS.enc.Base64.parse(encryptedMessage).toString(CryptoJS.enc.Utf8))
    const { encryptedKey, encryptedData } = decodedMessage
    const aesKey = this.rsaDecrypt(encryptedKey, privateKey)
    return this.aesDecrypt(encryptedData, aesKey)
  }
}

export const crypt = {
  /**
   * AES加密
   * @param word 需要加密的字符串，注：对象需要先转化为JSON字符串再加密
   * @param keyStr AES加密所需的密钥
   * @returns 加密后的字符串
   */
  encrypt(word: string, keyStr: string): string {
    // 将密钥转换为 Utf8 编码
    const key = CryptoJS.enc.Utf8.parse(keyStr)
    // 将需要加密的字符串转换为 Utf8 编码
    const srcs = CryptoJS.enc.Utf8.parse(word)
    // 使用 AES 加密，模式为 ECB，填充方式为 PKCS7
    const encrypted = CryptoJS.AES.encrypt(srcs, key, { mode: CryptoJS.mode.ECB, padding: CryptoJS.pad.Pkcs7 })
    // 将加密后的数据转换为字符串并返回
    return encrypted.toString()
  },

  /**
   * AES解密
   * @param encryptedWord 加密后的字符串
   * @param keyStr AES解密所需的密钥
   * @returns 解密后的字符串
   */
  decrypt(encryptedWord: string, keyStr: string): string {
    // 将密钥转换为 Utf8 编码
    const key = CryptoJS.enc.Utf8.parse(keyStr)
    // 使用 AES 解密，模式为 ECB，填充方式为 PKCS7
    const decrypted = CryptoJS.AES.decrypt(encryptedWord, key, { mode: CryptoJS.mode.ECB, padding: CryptoJS.pad.Pkcs7 })
    // 将解密后的数据转换为 Utf8 编码的字符串并返回
    return CryptoJS.enc.Utf8.stringify(decrypted).toString()
  }
}

/**
 * 过滤赋值为空的字段
 *
 * @param data data
 */
export function filterDataEmpty(data: any): void {
  for (const key in data) {
    if (data[key] === '') {
      delete data[key]
    }
  }
}

/**
 * 参数签名(object)
 *
 * @param data data
 * @param secretKey secretKey
 */
export function getSignedData(data: any, secretKey: string, encode = false): string {
  const keys: any = Object.keys(data).sort()
  let sign = ''
  const sortData: any = {}
  keys.forEach((key: string) => {
    let value = data[key]
    if (value === 'object') {
      value = JSON.stringify(value)
    }
    if (Array.isArray(value)) {
      value = JSON.stringify(value)
    }
    if (!(value === undefined || value === null || value === '')) {
      sortData[key] = value
    }
  })
  let source = stringify(sortData, {
    encode
  })
  source = source + '&' + secretKey
  sign = CryptoJS.MD5(source).toString().toLowerCase()
  // console.log(`签名参数: ${source}, 签名结果:${sign}`)
  return sign
}

/**
 * 参数签名(string)
 *
 * @param  data data
 * @param secretKey secretKey
 */
export function getSignedDataStr(data: any, secretKey: string): string {
  let source = stringify(data)
  const signSource = source + secretKey
  source += 'sign=' + CryptoJS.MD5(signSource).toString().toLowerCase()
  // console.log(`参数：${source}`);
  return source
}

/**
 * 获取时间戳（秒）
 */
export function getTimestamp(): number {
  return new Date().valueOf()
}

/**
 * 获取随机数(0-10000]
 */
export function getRandom(): number {
  return (Math.random() * 10000) << 0
}

/**
 * 获取指定位数随机数
 *
 * @param len 随机数长度
 */
export function randomNum(len: number): string {
  let salt = ''
  for (let i = 0; i < len; i++) {
    let tmp = String(Math.floor(Math.random() * 10))
    if (!tmp) {
      tmp = '2'
    }
    salt += tmp
  }
  return salt
}

/**
 * 获取当前语言 -- 返回给接口
 * @returns string 当前语言
 */
export const getRequestLanguage = (): any => {
  const language = LGSdk.getBrowserLocale()
  if (language === 'zh') {
    return 'zh-Hans'
  } else {
    return language as SupportedMultiLanguage
  }
}

/**
 * 获取别名
 * @param names
 * @param language
 */
export const getAliasName = (names: any, language: SupportedMultiLanguage) => {
  const alias = mergeLanguageObject(names)[language] ? `${mergeLanguageObject(names)[language]}` : ''
  return alias
}
